import { useState, useEffect, useMemo, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import clsx from 'clsx';
import {
  AgxErrorLabel,
  AgxRadio,
  AgxCaption,
  AgxLabel,
  Images,
  AgxDivider,
  AgxRow,
} from '@urbanx/agx-ui-components';
import { isEqual } from 'lodash-es';
import { useAppSelector } from 'hooks/useAppSelector';
import { formatCurrencyWithConditionalDecimals } from 'utils/formatNumber';
import { parseFloatReturnEmptyIfZero } from 'utils/parseFloatReturnEmptyIfZero';
import './TieredCommission.scss';
import { TieredCommission, TieredCommissionRow, TieredSystemType } from './types';
import { AgxButton, AgxColumn, AgxTextInput } from '@urbanx/agx.ui-components2';

const createSingleRateExampleRow = (
  first: boolean,
  last: boolean,
  lowerBound: string,
  upperBound: string,
  percentage: string
) => {
  if (first) {
    return `${percentage} of the actual sale price for a result achieved up to and including ${upperBound}`;
  } else if (last) {
    return `${percentage} of the actual sale price for any result achieved above ${lowerBound}`;
  } else {
    return `${percentage} of the actual sale price for a result achieved between ${lowerBound} and ${upperBound}`;
  }
};

const createMultiRateExample = (
  first: boolean,
  last: boolean,
  lowerBound: string,
  upperBound: string,
  percentage: string
) => {
  if (first) {
    return `${percentage} of the actual sale price for a result achieved up to and including ${upperBound}`;
  } else if (last) {
    return `A performance bonus of ${percentage} on any amount achieved over ${lowerBound}`;
  } else {
    return `${percentage} of any amount achieved between ${lowerBound} and ${upperBound} inclusive`;
  }
};

const tierSystemOptions = [
  {
    description: 'Single rate',
    value: TieredSystemType.SingleRate,
  },
  {
    description: 'Multi-rate',
    value: TieredSystemType.MultiRate,
  },
];

interface TieredCommissionProps {
  id: string;
  onValueChanged: (value: { id: string; value: TieredCommission }) => void;
  defaultValue?: TieredCommission;
  validate?: boolean;
  required?: boolean;
}

const TieredCommissionComponent = ({
  id,
  onValueChanged,
  defaultValue,
  validate,
  required,
}: TieredCommissionProps) => {
  const [tierType, setTierType] = useState(
    defaultValue?.TieredSystemType ?? TieredSystemType.SingleRate
  );
  const [tieredRows, setTieredRows] = useState<TieredCommissionRow[]>(
    () =>
      defaultValue?.Rows ?? [
        {
          id: uuidv4(),
          LowerBound: 0.0,
          UpperBound: 0.0,
          PercentageWithinBound: 0.0,
          Order: 0,
        },
        {
          id: uuidv4(),
          LowerBound: 0.0,
          UpperBound: 0.0,
          PercentageWithinBound: 0.0,
          Order: 1,
        },
      ]
  );

  const selectedForm = useAppSelector((state) => state.form.selectedForm);
  const availableForms = useAppSelector((state) => state.forms.availableForms);
  const formName = availableForms.find(
    (form) => form.type === selectedForm?.formType && isEqual(form.state, selectedForm?.state)
  )?.name;

  const [error, setError] = useState<Record<string, string>>({});

  useEffect(() => {
    const validationErrors: Record<string, string> = {};
    tieredRows.forEach((row, index) => {
      if (index > 0 && row.UpperBound && Number(row.UpperBound) <= row.LowerBound) {
        validationErrors[row.id ?? `row-${index}`] = `Value must be greater than ${row.LowerBound}`;
      }
    });
    setError(validationErrors);
  }, [tieredRows]);

  useEffect(() => {
    if (Object.keys(error).length > 0) return;

    onValueChanged({
      id,
      value: {
        Rows: tieredRows.map((row, rowNumber) => {
          return {
            ...row,
            LowerBound: row.LowerBound === undefined ? 0.0 : row.LowerBound,
            UpperBound: row.UpperBound === undefined ? 0.0 : row.UpperBound,
            PercentageWithinBound:
              row.PercentageWithinBound === undefined ? 0.0 : row.PercentageWithinBound,
            Order: rowNumber,
          };
        }),
        TieredSystemType: tierType,
      },
    });
  }, [tieredRows, tierType, error]);

  const updateRowUpperBound = useCallback(
    (rowNumber: number, value?: string) => {
      const parsedValue = Number(value?.replaceAll(',', '') ?? 0);

      const updatedRows = [...tieredRows];

      updatedRows[rowNumber] = {
        ...updatedRows[rowNumber],
        UpperBound: parsedValue,
      };

      if (updatedRows.length >= rowNumber + 2) {
        updatedRows[rowNumber + 1] = {
          ...updatedRows[rowNumber + 1],
          LowerBound: parsedValue ?? 0.0,
          UpperBound: updatedRows[rowNumber + 1].UpperBound,
        };
      }

      setTieredRows(updatedRows);
    },
    [tieredRows, setTieredRows]
  );

  const updateRowPercentage = useCallback(
    (rowNumber: number, value: string) => {
      const parsedValue = value !== undefined && value !== '' ? parseFloat(value) : undefined;
      const updatedRows = [...tieredRows];

      updatedRows[rowNumber] = {
        ...updatedRows[rowNumber],
        PercentageWithinBound: parsedValue ?? 0.0,
      };

      setTieredRows(updatedRows);
    },
    [tieredRows, setTieredRows]
  );

  const addRow = useCallback(() => {
    const previousTier = tieredRows[tieredRows.length - 1];

    const newTier = {
      id: uuidv4(),
      LowerBound: tieredRows.length === 1 ? Number(previousTier.UpperBound) : 0.0,
      UpperBound: undefined,
      PercentageWithinBound: 0.0,
      Order: tieredRows.length,
    } as TieredCommissionRow;

    setTieredRows([...tieredRows, newTier]);
  }, [tieredRows, setTieredRows]);

  const deleteRow = useCallback(
    (rowNumber: number) => {
      let rows = [...tieredRows];
      rows.splice(rowNumber, 1);

      // // Reset the upper bound of  the last row to be unbounded.
      if (rows.length > 1) {
        rows[rows.length - 1] = {
          ...rows[rows.length - 1],
          UpperBound: undefined,
        };
      }

      setTieredRows(rows);
    },
    [tieredRows, setTieredRows]
  );

  const example = useMemo(() => {
    if (tierType === null) {
      return null;
    }

    if (!tieredRows || tieredRows?.length < 1) {
      return null;
    }

    let tierRowsText = [];
    for (var i = 0; i < tieredRows.length; i++) {
      const { LowerBound, UpperBound, PercentageWithinBound } = tieredRows[i];

      const first = i === 0;
      const last = i === tieredRows.length - 1;

      const lowerBound =
        LowerBound && LowerBound > 0
          ? formatCurrencyWithConditionalDecimals(Number(LowerBound) + 1)
          : '$0.00';
      const upperBound =
        UpperBound && UpperBound > 0 ? formatCurrencyWithConditionalDecimals(UpperBound) : '$0.00';
      const percentage =
        PercentageWithinBound && PercentageWithinBound > 0
          ? `${Number(PercentageWithinBound)
              .toFixed(3)
              .replace(/\.?0+$/, '')}%`
          : '0%';

      if (tierType === TieredSystemType.SingleRate) {
        tierRowsText.push(
          createSingleRateExampleRow(first, last, lowerBound, upperBound, percentage)
        );
      } else if (tierType === TieredSystemType.MultiRate) {
        tierRowsText.push(createMultiRateExample(first, last, lowerBound, upperBound, percentage));
      }
    }

    return tierRowsText;
  }, [tieredRows, tierType]);

  return (
    <>
      <AgxColumn gap="lg" fill>
        <AgxLabel medium>Commission Structure</AgxLabel>
        <AgxColumn gap="lg" fill>
          <AgxDivider />
          <CommissionRows
            tieredRows={tieredRows}
            updateRowUpperBound={updateRowUpperBound}
            updateRowPercentage={updateRowPercentage}
            deleteRow={deleteRow}
            validate={validate}
            required={required}
            error={error}
          />
        </AgxColumn>
        <AgxDivider />
        <AgxButton
          id="addrowbutton"
          minimal
          text="ADD ROW"
          onClick={addRow}
          variant="hollow"
          size="small"
        />
        <AgxDivider />
      </AgxColumn>
      <AgxColumn gap="md" fill>
        <div className="agxRadio">
          <AgxLabel medium>Tier System</AgxLabel>
          <div className="agxRadio-group">
            {tierSystemOptions.map(({ description, value }) => (
              <AgxRadio
                key={value}
                id={value}
                label={description}
                onCheckChanged={() => setTierType(value)}
                checked={value === tierType}
              />
            ))}
          </div>
        </div>
        {example != null && (
          <AgxColumn gap="sm">
            <AgxCaption>{`This will appear on the ${formName} as:`}</AgxCaption>
            <br />
            {example.map((tr, index) => {
              return <AgxCaption key={`caption-${index}`}>{tr}</AgxCaption>;
            })}
          </AgxColumn>
        )}
      </AgxColumn>
    </>
  );
};

const CommissionRows = ({
  tieredRows,
  updateRowUpperBound,
  updateRowPercentage,
  deleteRow,
  validate,
  required,
  error,
}: {
  tieredRows: TieredCommissionRow[];
  updateRowUpperBound: (rowNumber: number, value?: string) => void;
  updateRowPercentage: (rowNumber: number, value: string) => void;
  deleteRow: (rowNumber: number) => void;
  validate?: boolean;
  required?: boolean;
  error: Record<string, string>;
}) => {
  if (!tieredRows || tieredRows.length === 0) return null;

  return tieredRows.map((row, rowNumber) => {
    return (
      <TieredRow
        key={row.id}
        row={row}
        rowNumber={rowNumber}
        numberOfRows={tieredRows.length}
        updateRowUpperBound={updateRowUpperBound}
        updateRowPercentage={updateRowPercentage}
        deleteRow={deleteRow}
        validate={validate}
        required={required}
        error={error}
      />
    );
  });
};

const TieredRow = ({
  row,
  rowNumber,
  numberOfRows,
  updateRowUpperBound,
  updateRowPercentage,
  deleteRow,
  validate,
  required,
  error,
}: {
  row: TieredCommissionRow;
  rowNumber: number;
  numberOfRows: number;
  updateRowUpperBound: (rowNumber: number, value?: string) => void;
  updateRowPercentage: (rowNumber: number, value: string) => void;
  deleteRow: (rowNumber: number) => void;
  validate?: boolean;
  required?: boolean;
  error: Record<string, string>;
}) => {
  const { id: rowId, LowerBound, UpperBound, PercentageWithinBound } = row;

  const isAndAboveRow = useMemo(
    () => rowNumber > 0 && rowNumber === numberOfRows - 1,
    [rowNumber, numberOfRows]
  );

  const tierValueClasses = clsx('tierValueInputs', isAndAboveRow && 'isAndAboveRow');

  const leftLabel =
    rowNumber === 0 ? (
      <AgxLabel extraClasses="commissionLowerBound">Up to and including</AgxLabel>
    ) : (
      <AgxLabel extraClasses="commissionLowerBound">
        {formatCurrencyWithConditionalDecimals(Number(LowerBound) + 1)}{' '}
        {rowNumber < numberOfRows - 1 ? 'to' : 'or more'}
      </AgxLabel>
    );

  return (
    <>
      <AgxColumn key={rowId} fill gap="md">
        <AgxColumn fill gap="md">
          <div className={tierValueClasses}>
            {leftLabel}
            {!isAndAboveRow && (
              <AgxTextInput
                id={`UpperBound-${rowNumber + 1}`}
                type="currency"
                key={rowNumber}
                noDecimalPoints={true}
                noHeader
                onInputValueChange={({ value }) => {
                  const parsedValue = Number(value.replaceAll(',', ''));
                  if (parsedValue != undefined) updateRowUpperBound(rowNumber, value ?? undefined);
                }}
                defaultValue={UpperBound?.toString()}
                stretch
                errorText={
                  validate && parseFloatReturnEmptyIfZero(UpperBound?.toString()) === ''
                    ? 'Enter valid value'
                    : ''
                }
                required={required}
                hideErrorDescription
              />
            )}
            <AgxLabel extraClasses="commissionAt">at</AgxLabel>
            <div className="commissionPercentage">
              <AgxTextInput
                id={`PercentageWithinBound-${rowNumber + 1}`}
                noHeader
                type={'percent'}
                percentDecimalPlaces={3}
                onInputValueChange={({ value }) => {
                  updateRowPercentage(rowNumber, value);
                }}
                defaultValue={
                  parseFloatReturnEmptyIfZero(PercentageWithinBound?.toString())?.toString() ?? ''
                }
                required={required}
                errorText={
                  validate && parseFloatReturnEmptyIfZero(PercentageWithinBound?.toString()) === ''
                    ? 'Percentage must be a valid number'
                    : undefined
                }
                hideErrorDescription
              />
            </div>
            {rowNumber > 1 && (
              <AgxRow centered justifyCenter extraClasses="commissionRemove">
                <AgxButton
                  id={`deleteRow-${rowNumber}`}
                  minimal
                  rightIcon={<Images.CrossFill />}
                  onClick={() => deleteRow(rowNumber)}
                  variant="naked"
                  text=""
                />
              </AgxRow>
            )}
          </div>
        </AgxColumn>
        {rowId && error[rowId] && <AgxErrorLabel error={error[rowId]} />}
      </AgxColumn>
      {rowNumber !== numberOfRows - 1 && <AgxDivider />}
    </>
  );
};

export default TieredCommissionComponent;
